import { Utils } from "shared/utils/Utils";
import { AccessStorage } from "../storage/AccessStorage";

import { Request } from "./core/olga/Request";
import { Environment } from "shared/core/Environment";

export class FlowAPI {
  static async request({ endpoint, data = {}, params = {}, method = "get" }) {
    return await Request.request({
      service: "flow",
      endpoint: endpoint || "",
      method: method || "get",
      data: data || {},
      params: params || {},
      ignoreVersion: true,
    });
  }

  static async requestAuthenticated({
    endpoint,
    data = {},
    params = {},
    method = "get",
  }) {
    return await Request.request({
      service: "flow",
      endpoint: endpoint || "",
      method: method || "get",
      headers: {
        Authorization: `Bearer ${AccessStorage.getAccessToken()}`,
      },
      data: data || {},
      params: params || {},
      ignoreVersion: true,
    });
  }

  static async getFlows(folderId = null) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows",
      params: {
        folder: folderId,
      },
    });
  }

  static async getFullFlows() {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/full",
    });
  }

  static async getArchivedFlows() {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/archived",
    });
  }

  static async getAudiences(state) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/audiences",
      params: {
        state,
      },
    });
  }

  static async archiveAudience(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/audiences/${id}/archive`,
      method: "put",
    });
  }

  static async getFlowById(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}`,
    });
  }

  static async getStepsByFlowId(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/steps`,
    });
  }

  static async getStepsHistorysByFlowId(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/steps/history`,
    });
  }

  static async getFlowReports(id, stepId) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/reports`,
      params: {
        stepId,
      },
    });
  }

  static async postFlow({ name, folder }) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows`,
      method: "post",
      data: {
        name,
        folder,
        boxes: [],
        links: [],
      },
    });
  }

  static async putStepsByFlowId(id, steps) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/steps`,
      method: "PUT",
      data: steps,
    });
  }

  static async putPublishFlow(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/publish`,
      method: "PUT",
    });
  }

  static async putArchiveFlow(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/archive`,
      method: "PUT",
    });
  }

  static async putPauseFlow(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/pause`,
      method: "PUT",
    });
  }

  static async putNameFlow(id, name) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}`,
      method: "PUT",
      data: {
        name,
      },
    });
  }

  static async putFlow(id, data) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}`,
      method: "PUT",
      data: data,
    });
  }

  static async getWhatsAppResourceTemplates({ environmentId }) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/resources/${Environment.id}/whatsapp/templates`,
      method: "GET",
    });
  }

  static async putEmailResource({ environmentId, file, mimeType }) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/resources/${environmentId}/email`,
      method: "PUT",
      data: {
        file64: file,
        mimeType,
      },
    });
  }

  static async putWhatsappResource({ environmentId, file, mimeType }) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/resources/${environmentId}/whatsapp`,
      method: "PUT",
      data: {
        file64: file,
        mimeType,
      },
    });
  }

  static async putAudienceResource({ environmentId, file, mimeType }) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/resources/${environmentId}/audience`,
      method: "PUT",
      data: {
        file64: file,
        mimeType,
      },
    });
  }

  static async postAudience(name, boxes, links) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/audiences`,
      method: "post",
      data: { name, boxes, links },
    });
  }

  static async sendSmsTest(environmentId, payload) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/send_test/SMS",
      method: "post",
      data: {
        ...payload,
        environmentId,
      },
    });
  }

  static async sendPushTest(environmentId, payload) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/send_test/PUSH",
      method: "post",
      data: {
        ...payload,
        environmentId,
      },
    });
  }

  static async sendEmailTest(
    environmentId,
    { template, subject, emails, senderAddress, senderName }
  ) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/send_test/EMAIL",
      method: "post",
      data: {
        environmentId,
        template,
        subject,
        emails,
        senderAddress,
        senderName,
      },
    });
  }

  static async sendWhatsAppTest(environmentId, payload) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/send_test/WHATSAPP",
      method: "post",
      data: {
        ...payload,
        environmentId,
      },
    });
  }

  static async sendMetaWhatsAppTest(environmentId, payload) {
    return await FlowAPI.requestAuthenticated({
      endpoint: "/flows/send_test/WHATSAPP/meta",
      method: "post",
      data: {
        ...payload,
        environmentId,
      },
    });
  }

  static async getFlowReportResult(reportId) {
    const response = await FlowAPI.requestAuthenticated({
      endpoint: `/reports/${reportId}`,
    });

    if (response.getErrors([]).length > 0) {
      console.log(response);
      throw new Error(response.getErrors()[0]);
    }

    const job = response.getData({});

    const link = job?.result?.data;

    return link;
  }

  static async getFlowReport(flowId, stepId, { type, period, format }) {
    const endpoint = [
      "",
      flowId,
      stepId ? "steps" : null,
      stepId,
      "reports",
      type,
      period,
      "export",
      format,
    ]
      .filter((p) => p !== null)
      .join("/");
    const response = await FlowAPI.requestAuthenticated({ endpoint });

    const { id } = response.getData({});
    return id;
  }

  static async generateFlowReport(flowId, stepId, { type, period, format }) {
    try {
      const reportId = await FlowAPI.getFlowReport(flowId, stepId, { type, period, format });

      let link = null;
      let searches = 12;

      while (searches > 0 && !link) {
        await Utils.sleep(5000);

        link = await FlowAPI.getFlowReportResult(reportId);
        searches -= 1;
      }

      if (!link) {
        console.error('Ultrapassou o tempo para gerar o relatório', reportId);
        return;
      }

      const el = document.createElement("a");
      el.setAttribute("href", link);
      el.setAttribute("target", "_blank");
      el.setAttribute("download", true);
      el.click();
    } catch (err) {
      console.error('Falha na geração do relatório', err);
    }

  }

  static async getCreativeReport(
    flowId,
    creativeId,
    { type, period, format }
  ) {

    const response = await FlowAPI.requestAuthenticated({
      endpoint: `/${flowId}/creatives/${creativeId}/reports/${type}/${period}/export/${format}`,
    });

    const link = response.getData({});
    if (!link) {
      return null;
    }

    const { id } = response.getData({});
    return id;
  }

  static async generateCreativeReport(
    flowId,
    creativeId,
    { type, period, format }
  ) {

    try {
      const reportId = await FlowAPI.getCreativeReport(
        flowId,
        creativeId,
        { type, period, format }
      );

      let link = null;
      let searches = 12;

      while (searches > 0 && !link) {
        await Utils.sleep(5000);

        link = await FlowAPI.getFlowReportResult(reportId);
        searches -= 1;
      }

      if (!link) {
        console.error('Ultrapassou o tempo para gerar o relatório', reportId);
        return;
      }

      const el = document.createElement("a");
      el.setAttribute("href", link);
      el.setAttribute("target", "_blank");
      el.setAttribute("download", true);
      el.click();
    } catch (err) {
      console.error('Falha na geração do relatório', err);
    }

  }

  static async getLastExecutionReport(flowId) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/${flowId}/boxes/counters`,
    });
  }

  static async preview(id) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${id}/preview`,
    });
  }

  static async moveFolder(sourceFolderId, destinationFolderId) {
    return FlowAPI.requestAuthenticated({
      endpoint: `/flows/folders/${sourceFolderId}`,
      method: "put",
      data: {
        destinationFolderId,
      },
    });
  }

  static async deleteFolder(folderId) {
    return FlowAPI.requestAuthenticated({
      endpoint: `/flows/folders/${folderId}`,
      method: "delete",
    });
  }

  static async importFlows(environmentId, content) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${environmentId}/import`,
      method: "post",
      data: {
        flowData: content,
      },
    });
  }

  static async importJsonFlows(environmentId, content) {
    return await FlowAPI.requestAuthenticated({
      endpoint: `/flows/${environmentId}/json_import`,
      method: "post",
      data: {
        flowData: content,
      },
    });
  }
}
